import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Briefcase, Search, Send, User } from "lucide-react";
import api from "../../api/axios";
import { RichTextEditor } from "./Editor";

export default function MessageComposer({
  companies,
  sendMode,
  setSendMode,
  newMsg,
  setNewMsg,
  editorState,
  onEditorChange,
  insideCase = false,
  dataCase,
  sendMessage,
  isSending = false,
  isTyping = false,
}) {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const lawyerCacheRef = useRef({});
  const clientCacheRef = useRef({});
  const activeRequestRef = useRef(0);
  const searchContainerRef = useRef(null);

  const isCaseClientLocked = insideCase && sendMode === "client";
  const isCompanyLocked = insideCase && sendMode === "client";

  // =====================
  // HELPERS
  // =====================
  const normalizeOption = (item, type) => {
    if (!item?.id) return null;
    return {
      id: item.id,
      type,
      name: item.name || "",
      surname: item.surname || "",
      email: item.email || "",
      company_id: item.company_id || null,
    };
  };

  const dedupeByKey = (items = []) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = `${item?.type}-${item?.id}`;
      if (!item?.id || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const resolveCaseCompany = useCallback(() => {
    if (!insideCase) return null;
    return (
      dataCase?.company_relation ||
      companies.find((c) => c?.company_name === dataCase?.company_name) ||
      null
    );
  }, [insideCase, dataCase, companies]);

  // =====================
  // EFFECTS
  // =====================

  // Set company when in case mode or company list loads
  useEffect(() => {
    if (!insideCase) return;
    const company = resolveCaseCompany();
    if (company?.id) setSelectedCompany(company);
  }, [insideCase, dataCase, companies, resolveCaseCompany]);

  // Reset lawyer cache when company list changes
  useEffect(() => {
    lawyerCacheRef.current = {};
    clientCacheRef.current = {};
  }, [companies]);

  const preloadClientsByCompany = useCallback(async (companyId) => {
    if (!companyId) return [];

    const cacheKey = String(companyId);
    if (clientCacheRef.current[cacheKey]) {
      return clientCacheRef.current[cacheKey];
    }

    const resp = await api.post(
      `${process.env.REACT_APP_API_URL}/users/searchclientsbycompanymessages`,
      { company_id: companyId },
    );

    const allClients = dedupeByKey(
      (resp?.data?.clients || [])
        .map((item) => normalizeOption(item, "client"))
        .filter(Boolean),
    );

    clientCacheRef.current[cacheKey] = allClients;
    return allClients;
  }, []);

  // Pre-select recipients based on sendMode + case context; clear on mode change
  useEffect(() => {
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);

    if (!insideCase) {
      setNewMsg((prev) => ({ ...prev, recipients: [] }));
      return;
    }

    if (sendMode === "client") {
      const caseClient = normalizeOption(dataCase?.clients_relation, "client");
      setNewMsg((prev) => ({
        ...prev,
        recipients: caseClient ? [caseClient] : [],
      }));
    } else if (sendMode === "lawyer") {
      const caseLawyer = normalizeOption(dataCase?.lawyers_relation, "lawyer");
      setNewMsg((prev) => ({
        ...prev,
        recipients: caseLawyer ? [caseLawyer] : [],
      }));
    }
  }, [insideCase, sendMode, dataCase, setNewMsg]);

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // =====================
  // SEARCH
  // =====================
  const doSearch = useCallback(
    async (query, companyId, mode) => {
      if (!companyId || !query.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
        setSearchLoading(false);
        return;
      }

      const reqId = ++activeRequestRef.current;
      setSearchLoading(true);

      try {
        let results = [];

        if (mode === "lawyer") {
          const cacheKey = String(companyId);
          let allLawyers = lawyerCacheRef.current[cacheKey];

          if (!allLawyers) {
            const resp = await api.post(
              `${process.env.REACT_APP_API_URL}/users/searchdatabycompany`,
              { company: companyId },
            );
            if (reqId !== activeRequestRef.current) return;

            allLawyers = dedupeByKey(
              (resp?.data?.lawyers || [])
                .map((item) => normalizeOption(item, "lawyer"))
                .filter(Boolean),
            );
            lawyerCacheRef.current[cacheKey] = allLawyers;
          }

          if (reqId !== activeRequestRef.current) return;

          const q = query.toLowerCase();
          results = allLawyers
            .filter((l) => `${l.name} ${l.surname}`.toLowerCase().includes(q))
            .slice(0, 10);
        } else {
          const cacheKey = String(companyId);
          let allClients = clientCacheRef.current[cacheKey];

          if (!allClients) {
            allClients = await preloadClientsByCompany(companyId);
          }

          if (reqId !== activeRequestRef.current) return;

          const q = query.toLowerCase();
          results = (allClients || [])
            .filter((c) =>
              `${c.name} ${c.surname} ${c.email || ""}`
                .toLowerCase()
                .includes(q),
            )
            .slice(0, 10);
        }

        if (reqId !== activeRequestRef.current) return;

        setSearchResults(results);
        setShowDropdown(true);
      } catch {
        if (reqId === activeRequestRef.current) {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } finally {
        if (reqId === activeRequestRef.current) setSearchLoading(false);
      }
    },
    [preloadClientsByCompany],
  );

  // Debounce trigger
  useEffect(() => {
    const t = setTimeout(() => {
      doSearch(searchQuery, selectedCompany?.id, sendMode);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, selectedCompany?.id, sendMode, doSearch]);

  // =====================
  // HANDLERS
  // =====================
  const handleCompanyChange = (_, value) => {
    activeRequestRef.current += 1;
    setSelectedCompany(value || null);
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
    setShowDropdown(false);

    if (sendMode === "client" && value?.id) {
      preloadClientsByCompany(value.id).catch(() => null);
    }
  };

  const addRecipient = (option) => {
    const already = (newMsg.recipients || []).some(
      (r) => r.id === option.id && r.type === option.type,
    );
    if (already) return;
    setNewMsg((prev) => ({
      ...prev,
      recipients: [...(prev.recipients || []), option],
    }));
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const removeRecipient = (option) => {
    setNewMsg((prev) => ({
      ...prev,
      recipients: (prev.recipients || []).filter(
        (r) => !(r.id === option.id && r.type === option.type),
      ),
    }));
  };

  // =====================
  // DERIVED
  // =====================
  const recipients = newMsg.recipients || [];
  const searchDisabled = isCaseClientLocked || !selectedCompany?.id;
  const searchPlaceholder = !selectedCompany?.id
    ? "Selecciona una empresa primero"
    : sendMode === "client"
      ? "Buscar cliente por nombre..."
      : "Buscar abogado por nombre...";

  // =====================
  // RENDER
  // =====================
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid #d9e3f2",
        background:
          "linear-gradient(145deg, #f8fbff 0%, #ffffff 38%, #f5f9ff 100%)",
      }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{
            justifyContent: "space-between",
            alignItems: { md: "center" },
          }}>
          <Typography variant="h4" mb={0} sx={{ color: "#16365d" }}>
            Nuevo mensaje
          </Typography>
          {insideCase && (
            <Chip
              label="Modo expediente"
              size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: "#e8f1ff",
                color: "#1b4c8d",
              }}
            />
          )}
        </Stack>

        <Typography variant="body2" sx={{ mt: 1, color: "#4f657f" }}>
          El remitente será el usuario logeado actualmente.
        </Typography>

        {/* Mode switch */}
        <Box
          display="flex"
          gap={1}
          mb={2}
          mt={1.5}
          flexWrap="wrap"
          sx={{
            backgroundColor: "#f0f5fc",
            border: "1px solid #d8e4f6",
            borderRadius: "12px",
            p: 1,
          }}>
          <FormControlLabel
            control={
              <Switch
                checked={sendMode === "lawyer"}
                onChange={() => setSendMode("lawyer")}
              />
            }
            label="Enviar Abogado"
          />
          <FormControlLabel
            control={
              <Switch
                checked={sendMode === "client"}
                onChange={() => setSendMode("client")}
                color="primary"
              />
            }
            label="Enviar Cliente"
          />
        </Box>

        {/* Company selector */}
        <Autocomplete
          options={companies}
          value={selectedCompany}
          disabled={isCompanyLocked}
          isOptionEqualToValue={(o, v) => o?.id === v?.id}
          getOptionLabel={(o) => o?.company_name || ""}
          onChange={handleCompanyChange}
          renderOption={(props, option) => {
            const { key: _k, ...rest } = props;
            return (
              <li key={option?.id} {...rest}>
                {option?.company_name || ""}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empresa"
              size="small"
              placeholder="Selecciona empresa"
              helperText={
                isCompanyLocked
                  ? "Empresa del expediente (bloqueada)"
                  : undefined
              }
            />
          )}
        />

        {/* Recipient search + chips */}
        <Box ref={searchContainerRef} sx={{ position: "relative", mt: 1.5 }}>
          {/* Chips of selected recipients */}
          {recipients.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {recipients.map((r) => {
                const locked = isCaseClientLocked && r.type === "client";
                return (
                  <Chip
                    key={`${r.type}-${r.id}`}
                    icon={
                      r.type === "client" ? (
                        <User size={13} />
                      ) : (
                        <Briefcase size={13} />
                      )
                    }
                    label={`${r.name} ${r.surname}`.trim()}
                    onDelete={locked ? undefined : () => removeRecipient(r)}
                    size="small"
                    sx={{
                      backgroundColor:
                        r.type === "client" ? "#e3f2fd" : "#f3e5f5",
                      color: r.type === "client" ? "#1565c0" : "#6a1b9a",
                      fontWeight: 500,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Search input */}
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            disabled={searchDisabled}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value.trim()) setShowDropdown(true);
            }}
            onFocus={() => {
              if (searchResults.length > 0) setShowDropdown(true);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={15} color="#9e9e9e" />
                </InputAdornment>
              ),
              endAdornment: searchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : undefined,
            }}
          />

          {/* Results dropdown */}
          {showDropdown && !searchLoading && (
            <Paper
              elevation={4}
              sx={{
                position: "absolute",
                width: "100%",
                zIndex: 1300,
                mt: "2px",
                maxHeight: 260,
                overflow: "auto",
                borderRadius: "8px",
                border: "1px solid #e8edf2",
              }}>
              {searchResults.length === 0 ? (
                <Box
                  sx={{
                    p: 1.5,
                    fontSize: 13,
                    color: "#9e9e9e",
                    textAlign: "center",
                  }}>
                  Sin resultados para &ldquo;{searchQuery}&rdquo;
                </Box>
              ) : (
                <List dense disablePadding>
                  {searchResults.map((option) => {
                    const isAdded = recipients.some(
                      (r) => r.id === option.id && r.type === option.type,
                    );
                    return (
                      <ListItem
                        key={`${option.type}-${option.id}`}
                        disablePadding>
                        <ListItemButton
                          onClick={() => addRecipient(option)}
                          disabled={isAdded}
                          sx={{
                            py: 0.6,
                            opacity: isAdded ? 0.5 : 1,
                            "&:hover": { backgroundColor: "#f4f8ff" },
                          }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            {option.type === "client" ? (
                              <User size={15} color="#1565c0" />
                            ) : (
                              <Briefcase size={15} color="#6a1b9a" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={`${option.name} ${option.surname}`.trim()}
                            primaryTypographyProps={{ fontSize: 13 }}
                          />
                          {isAdded && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#9e9e9e", ml: 1, fontSize: 11 }}>
                              AÃ±adido
                            </Typography>
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>
          )}
        </Box>

        {isCaseClientLocked && (
          <Typography
            variant="caption"
            sx={{ color: "#4f657f", display: "block", mt: 0.5 }}>
            El cliente del expediente está seleccionado automáticamente y
            bloqueado.
          </Typography>
        )}

        {/* Subject */}
        <TextField
          label="Asunto"
          fullWidth
          size="small"
          sx={{ my: 2 }}
          value={newMsg.subject}
          onChange={(e) => setNewMsg({ ...newMsg, subject: e.target.value })}
        />

        <RichTextEditor
          editorState={editorState}
          onEditorChange={onEditorChange}
        />

        <Button
          variant="contained"
          startIcon={<Send />}
          sx={{ mt: 2 }}
          disabled={isSending || isTyping}
          onClick={sendMessage}>
          {isSending ? "Enviando..." : "Enviar"}
        </Button>
      </CardContent>
    </Card>
  );
}
